import React, { useMemo, useState } from "react";
import { Modal, Button } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import {
  FaCreditCard, FaShoppingCart, FaNewspaper, FaFileInvoiceDollar,
  FaTruck, FaShieldAlt, FaExchangeAlt, FaRobot, FaBuilding
} from "react-icons/fa";

const TAG = ({ children }) => <span className="tech-tag">{children}</span>;
const INITIAL_VISIBLE = 4;

const ITEM_META = [
  { id: "bosch-fullstack",    icon: FaBuilding,       key: "bosch",      tech: ["React", "TypeScript", "JavaScript", "Java", "Spring Boot", "PostgreSQL", "Azure", "Kubernetes"] },
  { id: "fintech-payments",   icon: FaCreditCard,     key: "fintech",    tech: ["Java", "Spring Boot", "Kafka", "TCP/Netty", "Docker", "Kubernetes", "Azure", "MongoDB", "Grafana"] },
  { id: "ai-rag-chat-lab",    icon: FaRobot,          key: "rag",        tech: ["Python", "LLM APIs", "ChromaDB", "Embeddings", "React", "Prompt engineering", "Observability"], demoUrl: "https://rag.aprelkin.com" },
  { id: "retail-oms",         icon: FaShoppingCart,   key: "retail",     tech: ["Java", "Kotlin", "Spring Boot", "Kafka", "Kubernetes", "Docker", "GCP", "PostgreSQL", "MongoDB"] },
  { id: "publishing-platform",icon: FaNewspaper,      key: "publishing", tech: ["Java", "Python", "Kafka", "Kubernetes", "Docker", "AWS", "Elasticsearch"] },
  { id: "fintech-billing",    icon: FaFileInvoiceDollar, key: "billing", tech: ["Java", "Spring Boot", "Kafka", "MongoDB", "Docker", "Kubernetes", "Azure"] },
  { id: "fulfillment",        icon: FaTruck,          key: "fulfillment",tech: ["Java", "Spring Boot", "Kafka", "Kubernetes", "Docker", "MySQL", "GCP"] },
  { id: "fraud-analytics",    icon: FaShieldAlt,      key: "fraud",      tech: ["Python", "Java", "Machine Learning", "PostgreSQL"] },
  { id: "tng-consulting",     icon: FaExchangeAlt,    key: "consulting", tech: ["Java", "Spring", "JavaEE", "Apache Flink", "Oracle DB", "PostgreSQL", "Hadoop"] },
];

function CaseModal({ show, onHide, item }) {
  const { t } = useTranslation();
  if (!item) return null;

  return (
    <Modal show={show} onHide={onHide} centered size="lg" dialogClassName="case-modal">
      <Modal.Header closeButton>
        <Modal.Title>{item.title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="case-meta">
          <div><b>{t('projects.modal.context')}:</b> {item.industry}</div>
          <div><b>{t('projects.modal.period')}:</b> {item.period}</div>
          <div><b>{t('projects.modal.team')}:</b> {item.team}</div>
          <div><b>{t('projects.modal.role')}:</b> {item.role}</div>
        </div>
        <hr />
        <h6>{t('projects.modal.built')}</h6>
        <ul>{item.built.map((x, i) => <li key={i}>{x}</li>)}</ul>
        <h6>{t('projects.modal.contributions')}</h6>
        <ul>{item.contributions.map((x, i) => <li key={i}>{x}</li>)}</ul>
        {item.impact?.length ? (
          <>
            <h6>{t('projects.modal.outcome')}</h6>
            <ul>{item.impact.map((x, i) => <li key={i}>{x}</li>)}</ul>
          </>
        ) : null}
        <div className="case-tags">
          {item.tech.map((t) => <TAG key={t}>{t}</TAG>)}
        </div>
        <div className="small text-muted mt-3">{t('projects.modal.disclaimer')}</div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>{t('projects.modal.close')}</Button>
        <Button variant="primary" href="#contact" onClick={onHide}>{t('projects.modal.ask')}</Button>
      </Modal.Footer>
    </Modal>
  );
}

export default function SystemsSection() {
  const { t } = useTranslation();
  const [selected, setSelected] = useState(null);
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE);

  const items = useMemo(() => ITEM_META.map((meta) => ({
    ...meta,
    title:         t(`projects.items.${meta.key}.title`),
    industry:      t(`projects.items.${meta.key}.industry`),
    period:        t(`projects.items.${meta.key}.period`),
    team:          t(`projects.items.${meta.key}.team`),
    role:          t(`projects.items.${meta.key}.role`),
    built:         t(`projects.items.${meta.key}.built`,         { returnObjects: true }),
    contributions: t(`projects.items.${meta.key}.contributions`, { returnObjects: true }),
    impact:        t(`projects.items.${meta.key}.impact`,        { returnObjects: true }),
  })), [t]);

  const visibleItems = items.slice(0, visibleCount);

  return (
    <section id="projects" className="systems-section">
      <div className="container py-5">
        <div className="section-sticky-head text-center mb-4">
          <div className="section-eyebrow section-eyebrow--center">{t('nav.projects')}</div>
          <h2>{t('projects.heading')}</h2>
        </div>

        <div className="systems-grid">
          {visibleItems.map((it) => (
            <div key={it.id} className="system-card">
              <div className="system-card-head">
                <div className="system-title-row">
                  <it.icon className="system-title-icon" aria-hidden="true" />
                  <div className="system-title">{it.title}</div>
                </div>
                <div className="system-sub">{it.industry} | {it.period}</div>
              </div>

              <ul className="system-bullets">
                {Array.isArray(it.built) && it.built.slice(0, 3).map((x, i) => <li key={i}>{x}</li>)}
              </ul>

              <div className="system-tags">
                {it.tech.slice(0, 7).map((t) => <TAG key={t}>{t}</TAG>)}
              </div>

              <div className="system-actions">
                <button className="offer-btn offer-btn-sm" onClick={() => setSelected(it)}>
                  {t('projects.viewDetails')}
                </button>
                {it.demoUrl ? (
                  <a className="offer-btn offer-btn-sm" href={it.demoUrl} target="_blank" rel="noreferrer">
                    Demo
                  </a>
                ) : null}
                <a className="offer-btn offer-btn-sm" href="#contact">
                  {t('contact.heading')}
                </a>
              </div>
            </div>
          ))}
        </div>

        {visibleCount < items.length ? (
          <div className="text-center mt-4">
            <button
              className="offer-btn load-more-btn"
              onClick={() => setVisibleCount((v) => Math.min(v + 3, items.length))}
            >
              {t('projects.loadMore')}
            </button>
          </div>
        ) : null}

        <CaseModal show={!!selected} onHide={() => setSelected(null)} item={selected} />
      </div>
    </section>
  );
}
